import { BackendErrors } from '../models';
import { TranslationsKeys } from './i18n';
import {
  PaymentMethodState,
  PaymentPartnerType,
  ServiceType,
  VehicleType,
} from '../codegen/vehicles';
import { UnitType, TransactionState, Product } from '../codegen/transactions';
import { BookingStatus } from '../codegen/bookings';

type EnumTranslation<T extends string> = {
  [key in T]: TranslationsKeys;
};

export const SERVICE_TO_TRANSLATION_KEY: EnumTranslation<ServiceType> = {
  FUELING: 'common.serviceType.fueling',
  BREAKDOWN: 'common.serviceType.breakdown',
  PARKING: 'common.serviceType.parking',
  WASHING: 'common.serviceType.washing',
  CHARGING: 'common.serviceType.charging',
  OTHER: 'common.serviceType.otherCosts',
};

export const PAYMENT_PARTNER_TO_TRANSLATION_KEY: Record<
  PaymentPartnerType,
  string
> = {
  UTA_PAYMENT: 'UTA',
  MOCK_UTA_PAYMENT: 'UTA (Mock)',
  MOCK_FUELING_PAYMENT: 'MAN Pump & Pay',
  MAN_CHARGE_AND_GO_PAYMENT: 'MAN Charge&Go',
  DKV_PAYMENT: 'DKV',
};

export const VEHICLE_TYPE_TO_TRANSLATION_KEY: EnumTranslation<VehicleType> = {
  TRUCK: 'common.vehicleType.truck',
  BUS: 'common.vehicleType.bus',
  VAN: 'common.vehicleType.van',
  TRAILER: 'common.vehicleType.trailer',
  CAR: 'common.vehicleType.car',
};

export const VEHICLE_TYPE_PLURAL_TO_TRANSLATION_KEY: EnumTranslation<VehicleType> =
  {
    TRUCK: 'common.vehicleType.trucks',
    BUS: 'common.vehicleType.buses',
    VAN: 'common.vehicleType.vans',
    TRAILER: 'common.vehicleType.trailers',
    CAR: 'common.vehicleType.cars',
  };

export const TRANSACTION_STATE_TO_TRANSLATION_KEY: EnumTranslation<TransactionState> =
  {
    CREATED: 'common.transactionState.created',
    IN_PROGRESS: 'common.transactionState.inProgress',
    COMPLETED: 'common.transactionState.completed',
    DELIVERED: 'common.transactionState.delivered',
    FAILED: 'common.transactionState.failed',
    CANCELED: 'common.transactionState.canceled',
  };

export const BOOKING_STATUS_TO_TRANSLATION_KEY: EnumTranslation<BookingStatus> =
  {
    PLANNED: 'common.bookingStatus.planned',
    IN_PROGRESS: 'common.bookingStatus.inProgress',
    COMPLETED: 'common.bookingStatus.completed',
    CANCELED: 'common.bookingStatus.canceled',
  };

export const UNIT_TYPE_TO_TRANSLATION_KEY: EnumTranslation<UnitType> = {
  LITER: 'common.transaction.unit.liters',
  KG: 'common.transaction.unit.kg',
  PIECES: 'common.transaction.unit.pieces',
  KWH: 'common.transaction.unit.kwh',
  HOURS: 'common.transaction.unit.hours',
};

export const SERVICE_CARD_TYPE_TO_TRANSLATION_KEY: EnumTranslation<PaymentPartnerType> =
  {
    MOCK_FUELING_PAYMENT: 'common.serviceCardType.manFuelCard',
    UTA_PAYMENT: 'common.serviceCardType.utaFuelCard',
    MOCK_UTA_PAYMENT: 'common.serviceCardType.utaMockCard',
    MAN_CHARGE_AND_GO_PAYMENT: 'common.serviceCardType.manChargeAndGoCard',
    DKV_PAYMENT: 'common.serviceCardType.dkvCard',
  };

export const ERROR_CODE_TO_TRANSLATION_KEY: EnumTranslation<BackendErrors> = {
  INVALID_FUEL_CARD: 'onboarding.dialog.serviceCardForm.invalidCardError',
  EXTERNAL_LOGIN_ERROR: 'common.defaultErrorMessage',
  CARD_ALREADY_IN_USE_SAME_ACCOUNT:
    'onboarding.dialog.serviceCardForm.duplicateCard.errorMessage',
  CARD_ALREADY_IN_USE_OTHER_ACCOUNT:
    'onboarding.dialog.serviceCardForm.duplicateCardOtherAccount.errorMessage',
};

export const PAYMENT_METHOD_STATE_TO_TRANSLATION_KEY: EnumTranslation<PaymentMethodState> =
  {
    ACTIVE: 'common.paymentMethod.status.active',
    ONBOARDING_FAILURE: 'common.paymentMethod.status.failure',
    ONBOARDING_PENDING: 'common.paymentMethod.status.pending',
  };

export const TRANSACTION_PRODUCT_TO_TRANSLATION_KEY: EnumTranslation<Product> =
  {
    ADBLUE: 'common.transaction.product.adblue',
    ADBLUE_JERRYCAN: 'common.transaction.product.adblueJerrycan',
    BREAKDOWN_TYRES: 'common.transaction.product.breakdownTyres',
    BRIDGE_FEE: 'common.transaction.product.bridgeFee',
    OTHER: 'common.transaction.product.otherProduct',
    CHARGING_BLOCKING_FEE: 'common.transaction.product.chargingBlockingFee',
    CHARGING_SESSION: 'common.transaction.product.chargingSession',
    ENGINE_OIL: 'common.transaction.product.engineOil',
    FERRY_FARE: 'common.transaction.product.ferryFare',
    TAX: 'common.transaction.product.tax',
    DIESEL: 'common.transaction.product.diesel',
    BIO_DIESEL: 'common.transaction.product.bioDiesel',
    HVO30_DIESEL: 'common.transaction.product.hvo30Diesel',
    HEATING_OIL: 'common.transaction.product.heatingOil',
    PREMIUM_DIESEL: 'common.transaction.product.premiumDiesel',
    PETROL: 'common.transaction.product.petrol',
    SUPER_E5: 'common.transaction.product.superE5',
    PREMIUM_PETROL: 'common.transaction.product.premiumPetrol',
    MIX: 'common.transaction.product.mix',
    SUPER_E10: 'common.transaction.product.superE10',
    SUPER_E85: 'common.transaction.product.superE85',
    LIQUID_PETROL_GAS: 'common.transaction.product.liquidPetrolGas',
    NATURAL_GAS: 'common.transaction.product.naturalGas',
    LIQUID_NATURAL_GAS: 'common.transaction.product.liquidNaturalGas',
    LIQUID_BIO_GAS: 'common.transaction.product.liquidBioGas',
    HYDROGEN: 'common.transaction.product.hydrogen',
    GEAR_OIL: 'common.transaction.product.gearOil',
    PARTS_AND_ACCESSORIES: 'common.transaction.product.partsAndAccessories',
    GENERAL_MAINTENANCE: 'common.transaction.product.generalMaintenance',
    VEHICLE_PARTS: 'common.transaction.product.vehicleParts',
    TOWING_SERVICE: 'common.transaction.product.towingService',
    INSURANCE: 'common.transaction.product.insurance',
    TOLLING_FEE: 'common.transaction.product.tollingFee',
    TUNNEL_FEE: 'common.transaction.product.tunnelFee',
    UTILIZATION_SERVICE_FEE: 'common.transaction.product.utilizationServiceFee',
    PARKING_FEE: 'common.transaction.product.parkingFee',
    VEHICLE_CLEANING: 'common.transaction.product.vehicleCleaning',
    BREAKDOWN_REPAIR: 'common.transaction.product.breakdownRepair',
    BREAKDOWN_SERVICE_FEE: 'common.transaction.product.breakdownServiceFee',
  };
